import React, { useEffect, useState } from 'react';
import { Languages, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  LANGUAGE_OPTIONS,
  useTranslation,
  type AppLanguage,
} from '../../lib/i18n';

const LanguageSettings: React.FC = () => {
  const { language, setLanguage, t } = useTranslation();
  const [selected, setSelected] = useState<AppLanguage>(language);

  useEffect(() => {
    setSelected(language);
  }, [language]);

  const save = () => {
    setLanguage(selected);
    toast.success(t('settings.languageSaved'));
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50/80 to-white p-3 sm:rounded-xl sm:p-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 sm:h-11 sm:w-11 sm:rounded-xl">
            <Languages className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 sm:text-base">{t('settings.languageTitle')}</h3>
            <p className="mt-1 text-xs leading-relaxed text-zinc-600 sm:text-sm">{t('settings.languageHint')}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:gap-3 sm:grid-cols-3">
        {LANGUAGE_OPTIONS.map((opt) => {
          const on = selected === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSelected(opt.id)}
              className={`group relative flex flex-col items-start overflow-hidden rounded-lg border p-2.5 text-left transition-all sm:rounded-xl sm:p-4 ${
                on
                  ? 'border-emerald-400 bg-emerald-50/80 ring-1 ring-emerald-200 sm:ring-2'
                  : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <span className="flex w-full items-center justify-between gap-1">
                <span className="text-base font-semibold text-zinc-900 sm:text-lg">{opt.nativeLabel}</span>
                {on ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 sm:h-5 sm:w-5" /> : null}
              </span>
              <span className="mt-1 text-[11px] font-medium text-zinc-500 sm:text-sm">{t(opt.labelKey)}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={selected === language}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};

export default LanguageSettings;
